import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import SEO from "@/components/seo/SEO";
import { Eyebrow, AmbientOrbs } from "@/components/common/ui";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await submitContact({ ...form, lang });
      setSent(true);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title={lang === "nl" ? "Contact" : "Contact"} path="/contact" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
            <h1 className="mt-8 font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl">{t("contact.h1")}</h1>
            <p className="mt-8 max-w-md text-lg text-white/65">{t("contact.sub")}</p>
            <div className="mt-12 space-y-4 text-sm text-white/55">
              <div><span className="eyebrow mr-3">EU</span>Amsterdam · Rotterdam · Berlin</div>
              <div><span className="eyebrow mr-3">US</span>New York</div>
              <div><span className="eyebrow mr-3">Mail</span>hello@bhtsolutions.com</div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="glass rounded-2xl p-8 lg:col-span-7" data-testid="contact-form">
            <div className="grid gap-5 md:grid-cols-2">
              <Field id="name" label={t("contact.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="contact-name" />
              <Field id="email" type="email" label={t("contact.email")} value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-email" />
              <Field id="company" label={t("contact.company")} value={form.company} onChange={(v) => setForm({ ...form, company: v })} className="md:col-span-2" testId="contact-company" />
              <Field id="message" as="textarea" label={t("contact.message")} value={form.message} onChange={(v) => setForm({ ...form, message: v })} required className="md:col-span-2" rows={5} testId="contact-message" />
            </div>
            <button
              type="submit"
              disabled={busy || sent}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-bht-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-bht-void transition-transform hover:scale-[1.03] disabled:opacity-50"
              data-testid="contact-submit"
            >
              {busy ? "…" : sent ? "✓" : t("contact.submit")}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ id, label, value, onChange, type = "text", as, required, className = "", rows, testId }) {
  const base = "w-full rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-white placeholder:text-white/30 focus:border-bht-gold/60 focus:outline-none";
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow mb-2 block">{label}{required && " *"}</span>
      {as === "textarea" ? (
        <textarea id={id} required={required} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={base} data-testid={testId} />
      ) : (
        <input id={id} required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={base} data-testid={testId} />
      )}
    </label>
  );
}
